import { defineRouter } from '#q-app';
import {
  createMemoryHistory,
  createRouter,
  createWebHashHistory,
  createWebHistory,
} from 'vue-router';
import type {
  Router as RouterInstance,
  _RouterClassic as RouterClassic,
} from 'vue-router';
import routes from './routes';

// vue-router 5 resolves the public `Router` type through TypesConfig,
// so the app-specific inject/provide helpers are added there
declare module 'vue-router' {
  interface TypesConfig {
    Router: RouterClassic & {
      inject<T>(name: string): T;
      provide<T>(name: string, instance: T): void;
    };
  }
}

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

export default defineRouter(function (/* { store, ssrContext } */) {
  const createHistory = import.meta.env.QUASAR_SERVER
    ? createMemoryHistory
    : import.meta.env.QUASAR_VUE_ROUTER_MODE === 'history'
    ? createWebHistory
    : createWebHashHistory;

  const Router = createRouter({
    scrollBehavior: () => ({ left: 0, top: 0 }),
    routes: routes(/* { store } */),

    // Leave this as is and make changes in quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    history: createHistory(import.meta.env.QUASAR_VUE_ROUTER_BASE),
  }) as RouterInstance;

  Router.onError((error) => {
    if (import.meta.env.QUASAR_SERVER) {
      return;
    }

    const isChunkLoadFailed =
      /Loading chunk \d+ failed/.test(error.message) ||
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Unable to preload CSS');

    if (isChunkLoadFailed) {
      const lastReload = sessionStorage.getItem('last_chunk_reload');
      const now = Date.now();

      if (lastReload && now - parseInt(lastReload) < 10000) {
        console.error(
          'Chunk load failed again after reload. Giving up to prevent loop.'
        );
        return;
      }

      sessionStorage.setItem('last_chunk_reload', now.toString());

      window.location.reload();
    }
  });

  const deps: Record<string, unknown> = {};
  Router.inject = function inject<T>(name: string): T {
    return deps[name] as T;
  };
  Router.provide = function provide<T>(name: string, instance: T): void {
    deps[name] = instance;
  };

  return Router;
});
